/**
 * @fileoverview Programmatic auto-inserts for report templates (MOR-962).
 *
 * Some report content is purely structural and should never be AI-drafted:
 * the table of contents, the list of annexures, today's date. Template
 * wording marks these with `{{auto:<kind>}}` tokens. The generation action:
 *
 *  1. `normaliseAutoDirectives` — rewrites legacy `[Table of contents …]`
 *     style directives into the tokens, so older templates pick this up;
 *  2. `maskAutoInserts` — swaps each token for an opaque marker before the
 *     writer model sees the wording (the prompts tell it to keep markers);
 *  3. `resolveAutoInserts` — after drafting, replaces each marker with HTML
 *     built from the report's section list. If the model dropped a marker,
 *     the block is appended so the insert is never lost.
 *
 * Dependency-free: shared by the Convex action, the template editor (chip
 * picker + preview) and unit tests.
 */

export type AutoInsertKind = "toc" | "annexures" | "date";

export interface AutoInsertDef {
  kind: AutoInsertKind;
  /** Chip label in the editor. */
  label: string;
  /** One-line description for the picker. */
  description: string;
}

export const AUTO_INSERTS: ReadonlyArray<AutoInsertDef> = [
  {
    kind: "toc",
    label: "Table of contents",
    description: "Numbered list of the report's section headings",
  },
  {
    kind: "annexures",
    label: "List of annexures",
    description: "The template's attachment-only sections, in order",
  },
  {
    kind: "date",
    label: "Report date",
    description: "The date the report is generated",
  },
];

export const AUTO_INSERT_TOKEN_RE = /\{\{\s*auto\s*:\s*([a-z_]+)\s*\}\}/gi;

export function autoInsertToken(kind: AutoInsertKind): string {
  return `{{auto:${kind}}}`;
}

/** Opaque marker the model is told to reproduce verbatim. */
const MARKER = (kind: string) => `⟦AUTO:${kind}⟧`;
const MARKER_RE = /⟦AUTO:([a-z_]+)⟧/gi;

/**
 * Legacy directives such as "[Table of contents listing the report headings
 * and page numbers, followed by a list of annexures.]" become tokens.
 */
export function normaliseAutoDirectives(html: string): string {
  return html.replace(/\[([^\][]+)\]/g, (full, inner: string) => {
    const text = inner.toLowerCase();
    const wantsToc =
      /table of contents|contents (?:table|list|page)|list of (?:the )?(?:report )?(?:headings|sections)/.test(
        text,
      );
    const wantsAnnexures = /annexure|appendic|attachment/.test(text);
    if (!wantsToc && !wantsAnnexures) return full;
    // Only rewrite when the directive is *about* inserting a list, not a
    // table directive that merely mentions attachments.
    if (/^table with|^table —|^table -|columns?:/.test(text)) return full;
    const tokens: string[] = [];
    if (wantsToc) tokens.push(autoInsertToken("toc"));
    if (wantsAnnexures) tokens.push(autoInsertToken("annexures"));
    return tokens.join(" ");
  });
}

function isAutoInsertKind(value: string): value is AutoInsertKind {
  return AUTO_INSERTS.some((d) => d.kind === value);
}

/** Replace tokens with markers for the model. */
export function maskAutoInserts(html: string): {
  html: string;
  kinds: AutoInsertKind[];
} {
  const kinds: AutoInsertKind[] = [];
  const out = html.replace(AUTO_INSERT_TOKEN_RE, (_full, kind: string) => {
    const k = kind.toLowerCase();
    if (!isAutoInsertKind(k)) return "";
    if (!kinds.includes(k)) kinds.push(k);
    return MARKER(k);
  });
  return { html: out, kinds };
}

export interface AutoInsertSection {
  title: string;
  order: number;
  parentSectionId?: string | null;
  _id?: string;
  fillMode: "generate" | "attachment";
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Nested <ol> of the report's headings (top-level → nested sub-sections). */
export function renderTableOfContents(sections: AutoInsertSection[]): string {
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const top = ordered.filter((s) => !s.parentSectionId);
  if (top.length === 0) return "";
  const children = (parentId: string | undefined) =>
    parentId ? ordered.filter((s) => s.parentSectionId === parentId) : [];
  const items = top
    .map((s) => {
      const subs = children(s._id);
      const nested = subs.length
        ? `<ol>${subs
            .map((c) => `<li>${escapeHtml(c.title)}</li>`)
            .join("")}</ol>`
        : "";
      return `<li>${escapeHtml(s.title)}${nested}</li>`;
    })
    .join("");
  return `<ol data-morgan-auto="toc">${items}</ol>`;
}

/** Bullet list of attachment-only sections. */
export function renderAnnexures(sections: AutoInsertSection[]): string {
  const annexures = [...sections]
    .filter((s) => s.fillMode === "attachment")
    .sort((a, b) => a.order - b.order);
  if (annexures.length === 0) {
    return `<p data-morgan-auto="annexures"><em>There are no annexures to this report.</em></p>`;
  }
  return `<ul data-morgan-auto="annexures">${annexures
    .map((s) => `<li>${escapeHtml(s.title)}</li>`)
    .join("")}</ul>`;
}

export interface AutoInsertContext {
  sections: AutoInsertSection[];
  /** Pre-formatted report date. */
  today: string;
}

export function renderAutoInsert(
  kind: AutoInsertKind,
  ctx: AutoInsertContext,
): string {
  switch (kind) {
    case "toc":
      return renderTableOfContents(ctx.sections);
    case "annexures":
      return renderAnnexures(ctx.sections);
    case "date":
      return escapeHtml(ctx.today);
    default:
      return "";
  }
}

/**
 * Substitute markers (and any raw tokens that survived) with rendered HTML.
 * Markers the model lost are appended as blocks so nothing silently vanishes.
 * A marker sitting alone inside a <p> is lifted out so block HTML (a list)
 * isn't nested inside a paragraph.
 */
export function resolveAutoInserts(
  html: string,
  expectedKinds: AutoInsertKind[],
  ctx: AutoInsertContext,
): string {
  const seen = new Set<string>();
  const render = (kind: string) => {
    const k = kind.toLowerCase();
    seen.add(k);
    return isAutoInsertKind(k) ? renderAutoInsert(k, ctx) : "";
  };
  const isBlock = (kind: string) => kind.toLowerCase() !== "date";
  const substituted = html
    // <p>⟦AUTO:toc⟧</p> → block
    .replace(/<p[^>]*>\s*⟦AUTO:([a-z_]+)⟧\s*<\/p>/gi, (_f, kind: string) =>
      render(kind),
    )
    // A block marker sharing a <p> with prose: close the paragraph before
    // the block and reopen it after, so the list isn't nested in the <p>.
    .replace(
      /<p([^>]*)>([\s\S]*?)<\/p>/gi,
      (full, attrs: string, inner: string) => {
        if (!/⟦AUTO:[a-z_]+⟧/i.test(inner)) return full;
        const replaced = inner.replace(MARKER_RE, (_f, kind: string) =>
          isBlock(kind) ? `</p>${render(kind)}<p${attrs}>` : render(kind),
        );
        return `<p${attrs}>${replaced}</p>`;
      },
    )
    .replace(MARKER_RE, (_f, kind: string) => render(kind))
    .replace(
      /<p[^>]*>\s*\{\{\s*auto\s*:\s*([a-z_]+)\s*\}\}\s*<\/p>/gi,
      (_f, kind: string) => render(kind),
    )
    .replace(AUTO_INSERT_TOKEN_RE, (_f, kind: string) => render(kind));
  const out = expectedKinds.reduce(
    (acc, kind) =>
      seen.has(kind) ? acc : `${acc}\n${renderAutoInsert(kind, ctx)}`,
    substituted,
  );
  // Paragraphs emptied by the split above.
  return out.replace(/<p[^>]*>\s*<\/p>/gi, "");
}
